import { useCallback } from "react";
import { API_URL, useAuth } from "./AuthContext";

// Whop's embedded checkout looks the session up in this environment; it must match the
// API's WHOP_ENVIRONMENT
export const WHOP_ENVIRONMENT = process.env.REACT_APP_WHOP_ENVIRONMENT === "sandbox" ? "sandbox" : "production";

// Checkout calls (/api/payments) for the activation fee and funded accounts. Each
// resolves to the response body, or to { error } with a message the checkout page can
// show inline.
export default function usePayments() {
  const { token } = useAuth();

  const request = useCallback(
    async (path, body) => {
      try {
        const res = await fetch(`${API_URL}/api/payments${path}`, {
          method: body === undefined ? "GET" : "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            ...(body === undefined ? {} : { "Content-Type": "application/json" }),
          },
          body: body === undefined ? undefined : JSON.stringify(body),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) return { error: json.error || "Something went wrong. Please try again." };
        return json;
      } catch {
        return { error: "Couldn't reach the server. Check your connection and try again." };
      }
    },
    [token]
  );

  // The price the API will charge. kind: "activation" | "funded".
  // Resolves to { listPrice, cardFee, discount, total, code }
  const quote = useCallback(({ kind, method, code }) => request("/quote", { kind, method, code }), [request]);
  // Opens a hosted checkout: { checkout: { id, total, paymentUrl (Nexus) | purchaseUrl (Whop), ... } }.
  // `coin` (USDT | USDC) is the coin Nexus's widget opens on.
  const startCheckout = useCallback(
    ({ kind, method, code, coin }) => request("/checkout", { kind, method, code, coin }),
    [request]
  );
  // Charges the card entered in Whop's card fields: { checkout, clientSecret }, where
  // clientSecret finishes any 3-D Secure step
  const chargeCard = useCallback(
    ({ kind, code, confirmationToken, returnUrl }) =>
      request("/card", { kind, code, confirmationToken, returnUrl }),
    [request]
  );
  // Opens a Stripe PaymentIntent for the price: { checkout, clientSecret }, which Stripe's
  // Payment Element confirms
  const createCardIntent = useCallback(({ kind, code }) => request("/card-intent", { kind, code }), [request]);
  // { checkout: { id, status: "Pending" | "Paid" } }
  const getCheckout = useCallback((id) => request(`/checkout/${id}`), [request]);
  // Uses left on a limited discount code: { code, limit, remaining }
  const getPromo = useCallback((code) => request(`/promo/${encodeURIComponent(code)}`), [request]);

  return { quote, startCheckout, chargeCard, createCardIntent, getCheckout, getPromo };
}
